import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import Formatter from '../../../../util/formatter';
import _ from 'lodash';
import moment from 'moment/moment';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';
import {Divider, FormLabel, Grid} from '@mui/material';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import makeStyles from '@mui/styles/makeStyles';
import {deliriumData} from './deliriumData';

const useStyles = makeStyles((theme) => ({
    score: {
        backgroundColor: theme.palette.inPatient.inputBackground,
        borderRadius: '10px',
        maxWidth: 60,
        minWidth: 60,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '22px',
        marginLeft: 'auto',
        minHeight: 55,
    },
}));

export const DeliriumForm = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const firstUpdate = useRef(true);

    const tester = Formatter.formatUserFullName(userObject);

    const [values, setValues] = useState({
        cat1: '',
        cat2: '',
        cat3: '',
        cat4: '',
    });

    useEffect(() => {
        if (!_.isNil(props.selectedForm)) {
            setValues({
                ...props.selectedForm?.values,
            });
        }
    }, []);

    useEffect(() => {
        if (!firstUpdate.current) {
            const dateOfForm = moment().toISOString();
            const id = uuidv4() + '';
            const data = [{
                name: 'studies.delirium',
                value: {
                    id: id,
                    date: dateOfForm,
                    by: tester,
                    login: userObject?.userName,
                    totalScore: totalScore(),
                    lastScore: checkLastScore(),
                    values: values,
                    parentForm: props.parentForm,
                },
                noteToAddWithRefId: prepareNote(),
                referenceId: id,
            }];

            props.save(data);
        }
    }, [values]);

    const checkLastScore = () => {
        if (!_.isEmpty(props.patient?.studies?.delirium?.list)) {
            let lastScore;
            for (let i = 0; i < props.patient?.studies?.delirium?.list.length; i++) {
                if (!props.patient?.studies?.delirium?.list[i].canceled) {
                    lastScore = props.patient.studies.delirium.list[i]?.totalScore;
                    break;
                }

            }
            return lastScore ? lastScore : null;
        }
    };

    const totalScore = () => {
        return (values?.cat1?.score || 0) + (values?.cat2?.score || 0) + (values?.cat3?.score || 0) + (values?.cat4?.score || 0);
    };

    const findScore = (cat, onlyScore) => {
        // eslint-disable-next-line default-case
        switch (cat) {
            case 'Alertness':
                return onlyScore ? values?.cat1?.score : values?.cat1;
            case 'AMT4':
                return onlyScore ? values?.cat2?.score : values?.cat2;
            case 'Attention':
                return onlyScore ? values?.cat3?.score : values?.cat3;
            case 'Acute Change or Fluctuating Course':
                return onlyScore ? values?.cat4?.score : values?.cat4;
        }
    };

    const setStateScore = (name, value) => {
        firstUpdate.current = false;

        // eslint-disable-next-line default-case
        switch (name) {
            case 'Alertness': {
                setValues(prevState => ({...prevState, cat1: value}));
                break;
            }
            case 'AMT4': {
                setValues(prevState => ({...prevState, cat2: value}));
                break;
            }
            case 'Attention': {
                setValues(prevState => ({...prevState, cat3: value}));
                break;
            }
            case 'Acute Change or Fluctuating Course': {
                setValues(prevState => ({...prevState, cat4: value}));
                break;
            }
        }
    };

    const renderOutput = () => {
        const score = totalScore();
        if (score >= 4) {
            return 'Possible delirium +/- cognitive impairment';
        } else if (score <= 3 && score >= 1) {
            return 'Possible cognitive impairment';
        } else {
            return 'Delirium or severe cognitive impairment unlikely (but delirium still possible if [4] information incomplete)';
        }
    };

    const prepareNote = () => {
        const lastScore = checkLastScore();
        const total = totalScore();
        return Notes.getDeliriumNote(values, total, lastScore, renderOutput(), props.parentForm);
    };

    return (
        <>
            {deliriumData.map((element, index) =>
                <Fragment key={element?.category}>
                    {index > 0 &&
                        <Divider style={{margin: '10px 5px'}}/>
                    }

                    <Grid container direction="row">
                        <Grid item xs={12} style={{marginBottom: 10}}>
                            <FormLabel>
                                {element.text}
                            </FormLabel>
                        </Grid>

                        <Grid item xs={11}>
                            <AgyleDictionaryField
                                label={t('Choose one')}
                                getOptionLabel={option => option.text}
                                isOptionEqualToValue={(option, value) => option.text === value.text}
                                options={element.values}
                                value={findScore(element.category, false) || null}
                                onChange={(event, newValue) => setStateScore(element.category, newValue)}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={1} className={classes.score}>
                            {findScore(element.category, true) ?? '-'}
                        </Grid>
                    </Grid>
                </Fragment>
            )}

            <Grid container style={{marginTop: '15px'}}>
                <Grid item xs={12}>
                    <FormLabel>
                        {t('Outcome')}:
                    </FormLabel>

                    <FormLabel style={{float: 'right'}}>
                        {t('Total Score')}:
                    </FormLabel>
                </Grid>

                <Grid item xs={11}>
                    <div className={classes.description}>
                        {t(renderOutput())}
                    </div>
                </Grid>

                <Grid item xs={1}>
                    <div className={classes.score}>
                        {totalScore()}
                    </div>
                </Grid>
            </Grid>
        </>
    );
};